import { Typography, message } from 'antd';
import { Auth } from 'aws-amplify';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import shallow from 'zustand/shallow';

import { useAuthStore } from '../../stores';
import { FormPageProps, LocationState } from '../../typings';
import { getDefaultLocationState } from '../../utils';
import { CardPage } from '../components/CardPage';
import { LOGIN_ROUTE } from '../routes';

import { ConfirmForm } from './components';

export const ConfirmRegistration = ({ defaultPath }: FormPageProps) => {
  const { t } = useTranslation();
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { isAuthenticated } = useAuthStore(({ isAuthenticated }) => ({ isAuthenticated }), shallow);

  const params = new URLSearchParams(location.search);
  const email = params.get('email');

  const confirmRegistrationSubmit = React.useCallback(
    async (values) => {
      try {
        if (email) {
          const { code } = values;

          await Auth.confirmSignUp(email, code);

          message.success(t('CONFIRM_REGISTRATION_SUCCESS'));
          history.push(LOGIN_ROUTE);
        }
      } catch (error) {
        console.log('Confirm -> error', error);
        message.warn(error.message);
      }
    },
    [email, history, t]
  );

  const resendConfirmationCode = React.useCallback(async () => {
    try {
      if (email) {
        await Auth.resendSignUp(email);
        message.success(t('RESEND_CODE_SUCCESS'));
      }
    } catch (error) {
      console.log('Resend -> error', error);
      message.warn(error.message);
    }
  }, [email, t]);

  const [{ loading: confirmLoading }, handleConfirmRegistrationSubmit] = useAsyncFn(confirmRegistrationSubmit);
  const [{ loading: resendLoading }, handleResendConfirmationCode] = useAsyncFn(resendConfirmationCode);

  if (!email) return <Redirect to={LOGIN_ROUTE} />;

  const { from } = location.state ?? getDefaultLocationState(defaultPath);

  if (isAuthenticated) return <Redirect to={from.pathname} />;

  return (
    <CardPage title={t('CONFIRM_REGISTRATION_PAGE_TITLE')}>
      <Typography.Paragraph>
        {t('CONFIRM_REGISTRATION_PAGE_DESCRIPTION', { confirmRegistration: t('CONFIRM_REGISTRATION_BUTTON') })}
      </Typography.Paragraph>
      <ConfirmForm
        onFinish={handleConfirmRegistrationSubmit}
        loading={confirmLoading}
        resendConfirmationCode={handleResendConfirmationCode}
        resendCodeLoading={resendLoading}
      />
    </CardPage>
  );
};
